import { useCallback, useEffect, useState } from "react";

import { COUNTDOWN_SECONDS, VIDEO_CLIP_STEPS } from "../constants";
import type { VideoClipStep } from "../constants";

interface UseRecordingTimersOptions {
  onCountdownComplete: () => void;
  step: VideoClipStep;
}

interface UseRecordingTimersReturn {
  countdownValue: number;
  elapsedTime: number;
  resetCountdown: () => void;
  resetElapsedTime: () => void;
}

export const useRecordingTimers = ({
  onCountdownComplete,
  step,
}: UseRecordingTimersOptions): UseRecordingTimersReturn => {
  const [countdownValue, setCountdownValue] = useState(COUNTDOWN_SECONDS);
  const [elapsedTime, setElapsedTime] = useState(0);

  const resetCountdown = useCallback(
    () => setCountdownValue(COUNTDOWN_SECONDS),
    [],
  );
  const resetElapsedTime = useCallback(() => setElapsedTime(0), []);

  useEffect(() => {
    if (step !== VIDEO_CLIP_STEPS.COUNTDOWN) return;

    if (countdownValue === 0) {
      onCountdownComplete();
      return;
    }

    const timer = setTimeout(() => {
      setCountdownValue(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [step, countdownValue, onCountdownComplete]);

  useEffect(() => {
    if (step !== VIDEO_CLIP_STEPS.RECORDING) return;

    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [step]);

  return { countdownValue, elapsedTime, resetCountdown, resetElapsedTime };
};
